import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Mutation } from 'react-apollo';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import TenantPickerModal from './TenantPickerModal';

const TEMPLATE_CATEGORIES = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'succession', label: 'Succession' },
  { value: 'continuity', label: 'Continuity' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'vix', label: 'VIX' },
  { value: 'general', label: 'General' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  filterControl: {
    minWidth: 200,
  },
  tagFilterControl: {
    minWidth: 240,
  },
  table: {
    minWidth: 650,
  },
  tagChip: {
    margin: theme.spacing(0.25),
    height: 20,
    fontSize: '0.7rem',
  },
}));

const DELETE_TEMPLATE_MUTATION = gql`
  mutation DeleteEmailTemplate($id: ID!) {
    delete_email_template(id: $id) {
      success
      errors
    }
  }
`;

const AVAILABLE_TENANTS_QUERY = gql`
  query AvailableTenants {
    availableTenants {
      name
      host
    }
  }
`;

const TemplateList = ({ templates, loading, refetch, currentHost }) => {
  const classes = useStyles();
  const [pickerState, setPickerState] = useState({ open: false, templateId: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState([]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    templates.forEach(t => {
      (t.template_tag_list || []).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        template.name.toLowerCase().includes(term) ||
        (template.subject || '').toLowerCase().includes(term);
      const matchesCategory =
        categoryFilter === 'all' || template.category === categoryFilter;
      const matchesTags =
        tagFilter.length === 0 ||
        tagFilter.every(tag => (template.template_tag_list || []).includes(tag));
      return matchesSearch && matchesCategory && matchesTags;
    });
  }, [templates, searchTerm, categoryFilter, tagFilter]);

  const handleCreate = () => {
    window.location.href = '/admin/email_templates/new';
  };

  const handleEdit = (templateId) => {
    window.location.href = `/admin/email_templates/${templateId}/edit`;
  };

  if (loading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">Email Templates</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Template
        </Button>
      </div>

      <div className={classes.filters}>
        <TextField
          label="Search templates"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.filterControl}
        />
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Category"
          >
            <MenuItem value="all">All Categories</MenuItem>
            {TEMPLATE_CATEGORIES.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          options={allTags}
          value={tagFilter}
          onChange={(_e, newValue) => setTagFilter(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option}
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label="Filter by tags"
              placeholder={tagFilter.length === 0 ? 'All tags' : ''}
            />
          )}
          className={classes.tagFilterControl}
        />
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Layout</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {templates.length === 0
                      ? 'No templates found. Create your first template to get started.'
                      : 'No templates match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>{template.subject}</TableCell>
                  <TableCell style={{ textTransform: 'capitalize' }}>{template.category}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap">
                      {(template.template_tag_list || []).map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          className={classes.tagChip}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{template.layoutName}</TableCell>
                  <TableCell>
                    {new Date(template.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template.id)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip title="Duplicate to Tenant">
                      <IconButton
                        size="small"
                        onClick={() => setPickerState({ open: true, templateId: template.id })}
                      >
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Mutation
                      mutation={DELETE_TEMPLATE_MUTATION}
                      onCompleted={() => {
                        alert('Template deleted successfully');
                        refetch();
                      }}
                      onError={(error) => {
                        alert(`Error deleting template: ${error.message}`);
                      }}
                    >
                      {(deleteTemplate, { loading: deleting }) => (
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this template?')) {
                              deleteTemplate({ variables: { id: template.id } });
                            }
                          }}
                          disabled={deleting}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Mutation>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pickerState.templateId && (
        <Query query={AVAILABLE_TENANTS_QUERY}>
          {({ data }) => (
            <TenantPickerModal
              open={pickerState.open}
              onClose={() => setPickerState({ open: false, templateId: null })}
              tenants={data?.availableTenants || []}
              recordId={pickerState.templateId}
              recordType="template"
              currentHost={currentHost}
            />
          )}
        </Query>
      )}
    </div>
  );
};

TemplateList.propTypes = {
  templates:   PropTypes.array.isRequired,
  loading:     PropTypes.bool,
  refetch:     PropTypes.func.isRequired,
  currentHost: PropTypes.string,
};

TemplateList.defaultProps = {
  loading:     false,
  currentHost: '',
};

export default TemplateList;
